import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import StarCanvas from "./StarCanvas";
import { useTranslation } from "react-i18next";

const MemoStarCanvas = React.memo(StarCanvas);

const MemoButton = React.memo(({ children, onClick, className, isPrimary }) => {
  const buttonClasses = useMemo(() => {
    return `hero-button relative font-bold overflow-hidden transition-all duration-300 ease-in-out
      ${
        isPrimary
          ? "bg-gradient-to-br from-purple-700 via-purple-600 to-indigo-600 hover:from-purple-800 hover:via-purple-700 hover:to-indigo-700"
          : "text-white hover:bg-gray-900 hover:text-white backdrop-blur-sm"
      }
      shadow-lg hover:shadow-xl hover:z-10
      ${className}`;
  }, [isPrimary, className]);

  return (
    <button onClick={onClick} className={buttonClasses}>
      <span className="relative z-10">{children}</span>
      {isPrimary && (
        <div className="absolute inset-0 bg-white opacity-0 transition-opacity duration-300 hover:opacity-10" />
      )}
    </button>
  );
});

const staticStyles = {
  starCanvas: { zIndex: 1 },
  gradientOverlay: { zIndex: 2 },
  mainContent: { zIndex: 3 },
  scrollIndicatorContainer: { zIndex: 4 },
};

const HeroSection = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const [offset, setOffset] = useState(0);
  const [windowHeight, setWindowHeight] = useState(typeof window !== "undefined" ? window.innerHeight : 1000);
  const [showBackground, setShowBackground] = useState(false);

  const ticking = useRef(false);
  const animationFrame = useRef(null);
  const resizeTimeout = useRef(null);

  useEffect(() => {
  const img = new Image();
  img.src = '/imgs/Hero-portfolio.jpeg'; // Assicurati che il path sia identico
  img.onload = () => {
    // L'immagine è carica in cache, ora possiamo mostrarla fluidamente
    setShowBackground(true);
  };
}, []);

  useEffect(() => {
    if (location.state?.scrollToTop) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    if (location.state?.scrollToSection) {
      const el = document.getElementById(location.state.scrollToSection);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.state]);

  // ------------------- SCROLL & RESIZE -------------------
  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      animationFrame.current = requestAnimationFrame(() => {
        const newOffset = window.pageYOffset;
        if (Math.abs(newOffset - offset) > 0) setOffset(newOffset); // valore reale, niente arrotondamento
        ticking.current = false;
      });
      ticking.current = true;
    }
  }, [offset]);

  const handleResize = useCallback(() => {
    if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
    resizeTimeout.current = setTimeout(() => setWindowHeight(window.innerHeight), 100);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
    };
  }, [handleScroll, handleResize]);

  // ------------------- TRANSFORMS -------------------
  const transforms = useMemo(() => {
    return {
      imageOpacity: Math.max(0, 1 - offset / (windowHeight * 3)),
      backgroundTransform: `translate3d(0, ${offset * 0.3}px, 0)`,
      contentTransform: `translate3d(0, ${offset * 0.1}px, 0)`,
    };
  }, [offset, windowHeight]);

  /*  const handleCVClick = useCallback(() => {
    window.open("/Cv-Francesco Alessio Gentile.pdf", "_blank", "noopener,noreferrer");
  }, []); */

  const handleContactClick = useCallback((e) => {
    e.preventDefault();
    const contactSection = document.getElementById("contacts");
    if (contactSection) contactSection.scrollIntoView({ behavior: "smooth" });
  }, []);

  // ------------------- RENDER -------------------
  return (
    <section
      id="home"
      className="relative h-screen flex items-center justify-center overflow-hidden bg-black hero-section"
    >
      {/* BACKGROUND WRAPPER (entrata) */}
      <div
        className={`
          absolute inset-0
          transition-all duration-[1400ms] ease-out
          ${showBackground ? "opacity-100 scale-100 translate-y-0 blur-0" : "opacity-0 scale-105 translate-y-6 blur-sm"}
        `}
        style={{
          zIndex: 0,
          willChange: "opacity, transform, filter",
          backfaceVisibility: "hidden",
        }}
      >
        {/* BACKGROUND IMAGE (parallax) */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('/imgs/Hero-portfolio.jpeg')",
            transform: transforms.backgroundTransform,
            opacity: transforms.imageOpacity,
            willChange: "transform, opacity",
          }}
        />
      </div>

      {/* STAR CANVAS */}
      <MemoStarCanvas className="absolute inset-0 w-full h-full pointer-events-none" style={staticStyles.starCanvas} />

      {/* GRADIENT */}
      <div
        className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-black to-transparent"
        style={staticStyles.gradientOverlay}
      />

      {/* CONTENT */}
      <div
        className="relative text-center text-white px-6 pointer-events-auto transition-opacity duration-500"
        style={{ ...staticStyles.mainContent, transform: transforms.contentTransform }}
      >
        <h1 className="text-5xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight">
          <span
            className="block text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-300"
            data-aos="zoom-in"
            data-aos-duration="600"
          >
            {t("hero.title")}
          </span>
          <span
            className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mt-2"
            data-aos="zoom-in"
            data-aos-duration="600"
            data-aos-delay="400"
          >
            {t("hero.subtitle")}
          </span>
        </h1>

        <div
          className="
    mt-6 sm:mt-7 mx-auto text-center
    text-white md:text-gray-200
    text-lg sm:text-xl md:text-2xl
    leading-relaxed
    max-w-full md:max-w-3xl

    [filter:drop-shadow(0_3px_6px_rgba(0,0,0,0.65))]
    sm:[filter:drop-shadow(0_2px_4px_rgba(0,0,0,0.55))]
    md:[filter:none]
  "
          data-aos="fade-up"
          data-aos-delay="1500"
        >
          {t("hero.description")}
        </div>

        <div
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center pt-4"
          data-aos="fade-up"
          data-aos-delay="1200"
        >
          <div className="flex flex-col pt-4 sm:flex-row gap-4 justify-center items-center text-sm sm:text-base md:text-base">
            {/* <MemoButton
              onClick={handleCVClick}
              isPrimary
              className="w-38 sm:w-auto px-6 py-3 sm:px-8 sm:py-3 md:px-10 md:py-3 marvel-snap-button-shape transition-transform duration-300 hover:scale-110"
            >
              {t("hero.cv")}
            </MemoButton> */}

            <MemoButton
              onClick={handleContactClick}
              isPrimary={false}
              className="inline-block bg-gradient-to-br from-purple-700 via-purple-600 to-indigo-600
                                 hover:from-purple-800 hover:via-purple-700 hover:to-indigo-700
                                 text-white font-bold py-3 px-7 text-base sm:text-base md:text-sm shadow-md
                                 marvel-snap-button-shape transition-transform duration-300 ease-in-out hover:scale-105"
              /* className="w-38 sm:w-auto px-6 py-3 sm:px-8 sm:py-3 md:px-10 md:py-3 marvel-snap-button-shape backdrop-blur-md bg-gray-950/70 text-white shadow-lg transition-all duration-300 hover:scale-110 hover:bg-gray-950/80" */
            >
              {t("hero.contact")}
            </MemoButton>
          </div>
        </div>
      </div>

      {/* SCROLL INDICATOR */}
      <div
        className="absolute bottom-16 left-1/2 transform -translate-x-1/2"
        style={staticStyles.scrollIndicatorContainer}
      >
        <div className="w-6 h-10 border-2 border-white border-opacity-50 rounded-full flex justify-center backdrop-blur-sm">
          <div className="w-1 h-3 bg-white rounded-full mt-2 animate-scrollPulse" />
        </div>
      </div>
    </section>
  );
};

export default React.memo(HeroSection);
